export const CHAT_ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,text/plain,text/csv,application/json';

export type ChatAttachmentKind = 'image' | 'file';

export type PendingChatAttachment = {
  readonly id: string;
  readonly fileName: string;
  readonly kind: ChatAttachmentKind;
  /** Object URL for local image drafts; download URL for sent images. */
  readonly previewUrl?: string;
};

export type DraftAttachmentFile = {
  readonly id: string;
  readonly file: File;
  readonly kind: ChatAttachmentKind;
  readonly previewUrl: string | undefined;
};

const MIME_ALIASES: Readonly<Record<string, string>> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
  'image/svg': 'image/svg+xml',
  'text/x-csv': 'text/csv',
  'application/x-pdf': 'application/pdf',
};

const ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
]);

export function mimeTypeFromFileName(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.png')) {
    return 'image/png';
  }
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) {
    return 'image/jpeg';
  }
  if (lower.endsWith('.gif')) {
    return 'image/gif';
  }
  if (lower.endsWith('.webp')) {
    return 'image/webp';
  }
  if (lower.endsWith('.svg')) {
    return 'image/svg+xml';
  }
  if (lower.endsWith('.pdf')) {
    return 'application/pdf';
  }
  if (lower.endsWith('.json')) {
    return 'application/json';
  }
  if (lower.endsWith('.csv')) {
    return 'text/csv';
  }
  if (lower.endsWith('.txt')) {
    return 'text/plain';
  }
  return 'application/octet-stream';
}

export function canonicalAttachmentMimeType(mimeType: string, fileName: string): string {
  const raw = mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
  const aliased = MIME_ALIASES[raw] ?? raw;
  if (ALLOWED_MIME_TYPES.has(aliased)) {
    return aliased;
  }
  const fromName = mimeTypeFromFileName(fileName);
  if (ALLOWED_MIME_TYPES.has(fromName)) {
    return fromName;
  }
  return aliased;
}

export function isAllowedAttachmentMimeType(mimeType: string): boolean {
  return ALLOWED_MIME_TYPES.has(mimeType);
}

export function attachmentKindFromMimeType(mimeType: string): ChatAttachmentKind {
  return mimeType.startsWith('image/') ? 'image' : 'file';
}

export function createDraftAttachment(file: File): DraftAttachmentFile {
  const mimeType = canonicalAttachmentMimeType(file.type, file.name);
  const kind = attachmentKindFromMimeType(mimeType);
  return {
    id: crypto.randomUUID(),
    file,
    kind,
    previewUrl: kind === 'image' ? URL.createObjectURL(file) : undefined,
  };
}

export function revokeDraftAttachment(item: DraftAttachmentFile): void {
  if (item.previewUrl === undefined) {
    return;
  }
  URL.revokeObjectURL(item.previewUrl);
}

export function revokeDraftAttachments(items: readonly DraftAttachmentFile[]): void {
  items.forEach(revokeDraftAttachment);
}

export function pendingAttachmentFromDraft(item: DraftAttachmentFile): PendingChatAttachment {
  return {
    id: item.id,
    fileName: item.file.name,
    kind: item.kind,
    previewUrl: item.previewUrl,
  };
}
